// Global Instructions Rule Applied!
//
// One highlight set as a form tab (/period-highlights/forms/full/:id —
// docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md Phase 1; in-place edits and
// number tokens 2026-09-07). The human gate: read the narrative and the
// ordered items, edit wording, include / exclude, reorder — on a DRAFT or an
// APPROVED set, in place (the set's revision bumps; no re-approval) — then
// Approve or Reject a draft; Reformulate asks the agent for a new version.
// Figures are {tokens}: the form shows the resolved text and edits the raw
// text with a token picker; the backend refuses a stray literal on a
// books-derived item or in the narrative. Chrome: the MyDraftsPage title
// row, FormSection bodies, a read-only antd Table inside a FormSection, the
// fixed button grammar [Reject (danger)] [Reformulate] [Refresh numbers]
// [Edit narrative] and one closing action: [Approve (primary)] on a draft,
// [Sign off (primary)] when not reviewed, [Withdraw sign-off] when signed
// off, [Restore as new version (primary)] on a superseded version. Sign-off
// (owner, 2026-09-07): an auto-approved set is "not reviewed" until a person
// reads it and presses Sign off; an edit after the sign-off asks for it
// again, and so does a Refresh numbers that moved a printed figure beyond the
// tolerance (2026-10-28); a sign-off can be withdrawn; the reports show the
// status on every run. A superseded version comes back through Restore as
// new version: a draft copy (wording, hand edits, stored figures) that
// Approve makes current and signs off. A LIVE set (version 0, the open
// period's accumulating set the followers and trackers keep) is read-only
// here — its one action is [Finalise now (primary)]. A RETIRED live set (a
// closed quarter that has a current version, backlog 2.21) has no action: the
// list no longer shows it, and a direct link gets closed-quarter wording and
// the link to the current version.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Button, Input, Modal, Switch, Table, Tag, Tooltip, Typography } from 'antd'
import type { InputRef } from 'antd'
import type { TextAreaRef } from 'antd/es/input/TextArea'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStar } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { AgentMarkdown } from '../../../core/components/AgentMarkdown.tsx'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { updateTabTitle } from '../../../store/slices/tabSlice'
import type { CoverageLeftOut, CoverageNote, HighlightItemRecord, HighlightSetDetail, HighlightSetStaleness } from '../../../state_manager/data_models/period_highlights_model.ts'
import {
  HighlightCompanySummaryNoteSchema, HighlightMetricsRefreshNoteSchema, HighlightNarrativeRegenNoteSchema, HighlightSignOffWithdrawalSchema
} from '../../../state_manager/data_models/period_highlights_model.ts'
import {
  approveHighlightSet, benchHighlightSet, cloneHighlightSetDraft, editHighlightItem, editHighlightSetNarrative, fetchHighlightSetDetail,
  fetchHighlightSetStaleness, generateHighlights, reformulateHighlightSet, refreshHighlightSetMetrics,
  regenerateHighlightSetNarrative, rejectHighlightSet, reorderHighlightItems, signOffHighlightSet, withdrawHighlightSetSignOff
} from '../services/periodHighlightsService.ts'
import { EvidenceDrawer } from './EvidenceDrawer.tsx'
import { STATUS_TAG, describeCoverageGaps, finalisePayload, versionTag } from './PeriodHighlightsPage.tsx'

const { Title } = Typography
const token = () => getAccessToken(store.getState().auth)

const POLARITY_TAG: Record<string, string> = { positive: 'green', negative: 'red', neutral: 'default' }

/** A row of "Companies not included": coverage.unavailable plus the members
 *  still generating when the wait ran out (kind wait_expired). */
type LeftOutRow = Omit<CoverageLeftOut, 'kind'> & { kind: CoverageLeftOut['kind'] | 'wait_expired' }
const LEFT_OUT_TAG: Record<LeftOutRow['kind'], { color: string; label: string }> = {
  paused: { color: 'gold', label: 'Follower paused' },
  skipped: { color: 'default', label: 'Skipped' },
  failed: { color: 'red', label: 'Failed' },
  draft: { color: 'blue', label: 'Draft atom' },
  no_atom: { color: 'default', label: 'No atom' },
  wait_expired: { color: 'gold', label: 'Timed out' }
}
const NARRATIVE_MAX = 2000

interface HighlightSetFormProps {
  recordId?: string | null
}

interface ItemDraft {
  headline: string
  body: string
  short_text: string
}

type ItemField = keyof ItemDraft

interface MetricToken {
  key: string
  label: string
  display: string
}

/** An LP set's item carries the fund(s) the LP holds the company through in
 *  metrics.funds (frozen by the runner: name, share, fmv_end). */
const heldThrough = (r: HighlightItemRecord): Array<{ name: string; share: number | null }> => {
  const funds = (r.highlight_items__metrics as Record<string, unknown> | null | undefined)?.funds
  if (!Array.isArray(funds)) return []
  return funds
    .filter((f): f is Record<string, unknown> => Boolean(f) && typeof f === 'object')
    .map((f) => ({ name: String(f.name ?? ''), share: typeof f.share === 'number' ? f.share : null }))
    .filter((f) => f.name)
}

/** A pinned item (metrics.pinned): the period's new / follow-on investment
 *  introduction the quarterly report's New Investments section prints — always
 *  included, editable like any item, never excluded. */
const isPinned = (r: HighlightItemRecord): boolean =>
  Boolean((r.highlight_items__metrics as Record<string, unknown> | null | undefined)?.pinned)

/** The position layer (2026-10-27): an item whose figures are THIS scope's own
 *  holding — the fund's (or the investor's see-through) fair value, cost,
 *  investment or commitment — rather than a fact about the company whoever
 *  holds it. metrics.layer is written by the tracker's position pass. */
const isPositionItem = (r: HighlightItemRecord): boolean =>
  (r.highlight_items__metrics as Record<string, unknown> | null | undefined)?.layer === 'position'

/** A runner-up (metrics.bench, 2026-10-29): a candidate the ranking offered
 *  and the agent did not select, stored excluded after the selection so the
 *  reviewer can swap it in under the included cap. The marker stays once it
 *  is included — it is the row's provenance. */
const isBench = (r: HighlightItemRecord): boolean =>
  Boolean((r.highlight_items__metrics as Record<string, unknown> | null | undefined)?.bench)

/** Client-side preview of the raw text: every {token} the catalogue knows
 *  becomes its display value (the backend renders the same way). */
const previewTokens = (text: string, tokens: MetricToken[]): string =>
  text.replace(/\{([a-z][a-z0-9_]*)\}/g, (whole, key: string) => tokens.find((t) => t.key === key)?.display ?? whole)

/** Insert `{key}` at the caret of an antd Input / TextArea (falls back to the end). */
const insertAtCaret = (
  el: HTMLInputElement | HTMLTextAreaElement | null | undefined, value: string, key: string
): { next: string; caret: number } => {
  const snippet = `{${key}}`
  const start = el?.selectionStart ?? value.length
  const end = el?.selectionEnd ?? value.length
  return { next: value.slice(0, start) + snippet + value.slice(end), caret: start + snippet.length }
}

/** The token picker row shared by the item and narrative editors. */
const TokenChips: React.FC<{ tokens: MetricToken[]; onPick: (key: string) => void; disabled?: boolean }> = ({ tokens, onPick, disabled }) => {
  if (!tokens.length) return <div className='text-xs text-gray-400'>No figure tokens on this record.</div>
  return (
    <div className='flex flex-wrap gap-1'>
      {tokens.map((t) => (
        <Tooltip key={t.key} title={<span>{`{${t.key}}`} — {t.label}</span>}>
          <Tag
            color='blue'
            className={disabled ? 'opacity-60' : 'cursor-pointer'}
            onClick={() => { if (!disabled) onPick(t.key) }}
          >
            {t.display}
          </Tag>
        </Tooltip>
      ))}
    </div>
  )
}

export const HighlightSetForm: React.FC<HighlightSetFormProps> = ({ recordId }) => {
  const dispatch = useDispatch()
  const { openFormInTab } = useTabManagerContext()
  const [detail, setDetail] = useState<HighlightSetDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [evidenceFor, setEvidenceFor] = useState<HighlightItemRecord | null>(null)
  const [editing, setEditing] = useState<HighlightItemRecord | null>(null)
  const [draft, setDraft] = useState<ItemDraft>({ headline: '', body: '', short_text: '' })
  const [activeField, setActiveField] = useState<ItemField>('headline')
  const [narrativeOpen, setNarrativeOpen] = useState(false)
  const [narrativeDraft, setNarrativeDraft] = useState('')
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [staleness, setStaleness] = useState<HighlightSetStaleness | null>(null)
  const [modal, contextHolder] = Modal.useModal()
  const headlineRef = useRef<InputRef>(null)
  const bodyRef = useRef<TextAreaRef>(null)
  const shortRef = useRef<TextAreaRef>(null)
  const narrativeRef = useRef<TextAreaRef>(null)

  const load = useCallback(async () => {
    if (!recordId) return
    setLoading(true)
    try {
      const d = await fetchHighlightSetDetail(recordId, token())
      setDetail(d)
      dispatch(updateTabTitle({
        key: `period-highlights-form-edit-${recordId}`,
        title: `${d.set.prog__scope_name ?? 'Highlights'} · ${d.set.prog__period_label} v${d.set.highlight_sets__version}`
      }))
      // Staleness (Phase 5, item-aware 2026-09-07): recomputes the figures
      // through the same readers — a heavier read than the detail, so it
      // lands after the form and never blocks or fails it. A live set is
      // measured to today by its own runs, so there is nothing to compare.
      if (d.set.highlight_sets__status !== 'rejected' && d.set.highlight_sets__status !== 'live') {
        fetchHighlightSetStaleness(recordId, token()).then(setStaleness).catch(() => setStaleness(null))
      }
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to load the highlight set')
    } finally {
      setLoading(false)
    }
  }, [recordId, dispatch])

  useEffect(() => { load() }, [load])
  const activateRef = useRefreshOnActivate(load)

  const set = detail?.set
  const isDraft = set?.highlight_sets__status === 'draft'
  // The open period's accumulating set: agent-owned, read-only here.
  const isLive = set?.highlight_sets__status === 'live'
  // A closed quarter's live set once the quarter has a current version: no
  // agent feeds it and no report reads it (prog__live_retired, SQL-owned).
  const isRetiredLive = isLive && Boolean(set?.prog__live_retired)
  // Owner decision 2026-09-07: draft AND approved sets are edited in place.
  const isEditable = isDraft || set?.highlight_sets__status === 'approved'
  const caps = detail?.caps ?? { headline: 120, body: 400, short_text: 220, included: 8 }
  const items = useMemo(() => detail?.items ?? [], [detail])
  // The selection (included, pinned first by sequence) and the rest — the
  // runners-up and anything the reviewer excluded (2026-10-29). At most
  // caps.included non-pinned items may be selected: the number the reports
  // print, enforced by the RPC and disabled here before it refuses.
  const selectedRows = useMemo(() => items.filter((i) => i.highlight_items__status === 'included'), [items])
  const benchRows = useMemo(() => items.filter((i) => i.highlight_items__status !== 'included'), [items])
  const selectedCount = selectedRows.filter((i) => !isPinned(i)).length
  const pinnedCount = selectedRows.length - selectedCount
  const includedCap = caps.included
  const atCap = selectedCount >= includedCap
  const setTokens = useMemo<MetricToken[]>(() => (set?.prog__metric_tokens ?? []) as MetricToken[], [set])
  const itemStaleness = useMemo(() => {
    const byId: Record<string, { stale_category: boolean; moved: number }> = {}
    for (const it of staleness?.items ?? []) byId[it.item_id] = { stale_category: it.stale_category, moved: it.deltas.length }
    return byId
  }, [staleness])

  const run = async (fn: () => Promise<void>, done: string) => {
    setBusy(true)
    try {
      await fn()
      if (done) toast.success(done)
      await load()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'The action failed')
    } finally {
      setBusy(false)
    }
  }

  // Reorder within the row's own group; the RPC takes the whole list, so the
  // selection always resequences ahead of the runners-up.
  const move = (item: HighlightItemRecord, direction: -1 | 1) => {
    const inSelection = item.highlight_items__status === 'included'
    const group = (inSelection ? selectedRows : benchRows).map((i) => i.highlight_items__id)
    const idx = group.indexOf(item.highlight_items__id)
    const target = idx + direction
    if (idx < 0 || target < 0 || target >= group.length) return
    const next = [...group]
    next.splice(idx, 1)
    next.splice(target, 0, item.highlight_items__id)
    const other = (inSelection ? benchRows : selectedRows).map((i) => i.highlight_items__id)
    const all = inSelection ? [...next, ...other] : [...other, ...next]
    void run(() => reorderHighlightItems(set!.highlight_sets__id, all, token()), '')
  }

  // Add runners-up (2026-10-29): a queued, model-free pass of the scope's
  // tracker that appends the next candidates as excluded rows; nothing a
  // report prints changes, so a sign-off stands.
  const onBench = () => {
    modal.confirm({
      title: benchRows.some(isBench) ? 'Refresh the runners-up?' : 'Add runners-up to this version?',
      content: 'The agent re-reads the period’s company atoms and this scope’s positions, and appends the next candidates ' +
        'after the selected highlights as excluded runners-up you can swap in. Runners-up you already selected stay; ' +
        'the ones still excluded are replaced. Nothing a report prints changes.',
      okText: 'Add runners-up',
      onOk: () => run(async () => {
        const r = await benchHighlightSet(set!.highlight_sets__id, token())
        const status = r?.enqueue?.status
        toast.info(status === 'deferred' ? 'Queued behind the current run for this scope — the runners-up follow it'
          : 'Runners-up queued — they appear on this version once the pass has run')
      }, '')
    })
  }

  const startEdit = (item: HighlightItemRecord) => {
    setEditing(item)
    setActiveField('headline')
    setDraft({
      headline: item.highlight_items__headline,
      body: item.highlight_items__body ?? '',
      short_text: item.highlight_items__short_text ?? ''
    })
  }

  const fieldElement = (field: ItemField): HTMLInputElement | HTMLTextAreaElement | null | undefined =>
    field === 'headline' ? headlineRef.current?.input : field === 'body' ? bodyRef.current?.resizableTextArea?.textArea : shortRef.current?.resizableTextArea?.textArea

  const pickItemToken = (key: string) => {
    const el = fieldElement(activeField)
    const { next, caret } = insertAtCaret(el, draft[activeField], key)
    setDraft({ ...draft, [activeField]: next })
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(caret, caret) } })
  }

  const saveEdit = () => {
    if (!editing) return
    void run(async () => {
      const r = await editHighlightItem(editing.highlight_items__id, {
        headline: draft.headline.trim(),
        body: draft.body.trim() || null,
        short_text: draft.short_text.trim() || null
      }, token())
      setEditing(null)
      for (const w of r.warnings ?? []) toast.warning(w)
    }, 'Item saved')
  }

  const startNarrative = () => {
    setNarrativeDraft(set?.highlight_sets__narrative ?? '')
    setNarrativeOpen(true)
  }

  const pickNarrativeToken = (key: string) => {
    const el = narrativeRef.current?.resizableTextArea?.textArea
    const { next, caret } = insertAtCaret(el, narrativeDraft, key)
    setNarrativeDraft(next)
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(caret, caret) } })
  }

  const saveNarrative = () => {
    void run(async () => {
      const r = await editHighlightSetNarrative(set!.highlight_sets__id, narrativeDraft.trim() || null, token())
      setNarrativeOpen(false)
      // A typed figure is saved and warned, never refused (owner 2026-10-29).
      for (const w of r.warnings ?? []) toast.warning(w)
    }, 'Narrative saved')
  }

  const reviewStatus = set?.prog__review_status ?? null
  const needsSignOff = reviewStatus === 'unreviewed' || reviewStatus === 'edited_since_sign_off'
  const isSuperseded = set?.highlight_sets__status === 'superseded'
  // The sign-off lifecycle's notes on params (2026-10-28): the last sign-off
  // withdrawn, and the last Refresh numbers (did it ask for a new sign-off?).
  const withdrawal = useMemo(() => {
    const parsed = HighlightSignOffWithdrawalSchema.safeParse(set?.highlight_sets__params?.sign_off_withdrawn)
    return parsed.success ? parsed.data : null
  }, [set])
  // Narrative regeneration (backlog 2.21): the last pass, and whether the
  // selection moved after the narrative was last written — an include /
  // exclude stamps params.selection_changed_at; the finalise, a hand edit and
  // a regeneration date the narrative (narrative_written_at, else generated_at).
  const narrativeRegen = useMemo(() => {
    const parsed = HighlightNarrativeRegenNoteSchema.safeParse(set?.highlight_sets__params?.narrative_regenerated)
    return parsed.success ? parsed.data : null
  }, [set])
  const selectionChangedAfterNarrative = useMemo(() => {
    const changed = set?.highlight_sets__params?.selection_changed_at
    const written = set?.highlight_sets__params?.narrative_written_at ?? set?.highlight_sets__generated_at
    if (typeof changed !== 'string' || typeof written !== 'string') return false
    return dayjs(changed).isAfter(dayjs(written))
  }, [set])

  const onRegenerateNarrative = () => {
    modal.confirm({
      title: 'Regenerate the narrative?',
      content: 'The agent rewrites the narrative over the highlights selected now — those stories and only those. ' +
        'The current narrative is replaced, including any hand edits to it; the items stay as they are. ' +
        'A signed-off set asks for sign-off again.',
      okText: 'Regenerate narrative',
      onOk: () => run(async () => {
        const r = await regenerateHighlightSetNarrative(set!.highlight_sets__id, token())
        const status = r?.enqueue?.status
        toast.info(status === 'deferred' ? 'Queued behind the current run for this scope — the narrative follows it'
          : 'Narrative regeneration queued — the new narrative appears on this version once the run has landed')
      }, '')
    })
  }
  const lastRefresh = useMemo(() => {
    const parsed = HighlightMetricsRefreshNoteSchema.safeParse(set?.highlight_sets__params?.metrics_refresh)
    return parsed.success ? parsed.data : null
  }, [set])
  // A company update (single-company scopes, 2026-10-28): the scope held one
  // company in the period (cfg company_summary_max_companies), so the
  // tracker wrote a sectioned summary of its quarter instead of ranked
  // highlights. Stored on the version (params.layout) — never recomputed.
  const isCompanySummary = set?.highlight_sets__params?.layout === 'company_summary'
  const companySummaryNote = useMemo(() => {
    const parsed = HighlightCompanySummaryNoteSchema.safeParse(set?.highlight_sets__params?.company_summary)
    return parsed.success ? parsed.data : null
  }, [set])
  // The narrative cap this version is edited under (the row emits it: a
  // company update gets cfg company_summary_narrative_max_chars).
  const narrativeMax = set?.prog__narrative_max_chars ?? NARRATIVE_MAX
  // Edited since sign-off BECAUSE a refresh moved a printed figure: the
  // refresh that bumped the revision came after the sign-off.
  const figuresMovedSinceSignOff = Boolean(
    lastRefresh?.revision_bumped && lastRefresh.at && set?.highlight_sets__signed_off_at &&
    dayjs(lastRefresh.at).isAfter(dayjs(set.highlight_sets__signed_off_at))
  )

  const onRefreshMetrics = () => {
    const wasSignedOff = reviewStatus === 'signed_off'
    void run(async () => {
      const r = await refreshHighlightSetMetrics(set!.highlight_sets__id, token())
      const stale = r.written.items_stale_category
      if (stale > 0) toast.warning(`${stale} item${stale === 1 ? '' : 's'} no longer supported by the books — left out of reports until the set is reformulated`)
      // Backlog 2.24 (owner, 2026-10-28): a printed figure that moved beyond
      // the tolerance asks for a new sign-off.
      if (r.written.revision_bumped && wasSignedOff) {
        const moved = r.written.figures_moved
        const lost = r.written.items_newly_stale
        toast.warning(
          [moved > 0 ? `${moved} figure${moved === 1 ? '' : 's'} moved by more than ${r.check.tolerance_pct}%` : '',
            lost > 0 ? `${lost} item${lost === 1 ? '' : 's'} lost ${lost === 1 ? 'its' : 'their'} category` : '']
            .filter(Boolean).join(' and ') + ' — the sign-off needs renewing: re-read the set and press Sign off.'
        )
      }
    }, 'Figures refreshed from the books')
  }

  const onWithdrawSignOff = () => {
    modal.confirm({
      title: 'Withdraw the sign-off?',
      content: `Reports show these highlights as not reviewed again until someone signs off.${set?.prog__signed_off_by_name ? ` The set keeps a note of ${set.prog__signed_off_by_name}’s sign-off${set.highlight_sets__signed_off_at ? ` of ${dayjs(set.highlight_sets__signed_off_at).format('MM/DD/YYYY')}` : ''} and of who withdrew it.` : ''}`,
      okText: 'Withdraw sign-off',
      onOk: () => run(async () => { await withdrawHighlightSetSignOff(set!.highlight_sets__id, token()) }, 'Sign-off withdrawn')
    })
  }

  // Restore as new version (backlog 2.23): a superseded version — say the one
  // that was signed off before a regenerate replaced it — comes back as a
  // draft copy; Approve makes it current and signs it off.
  const onRestore = () => {
    const openDraft = (detail?.versions ?? []).find((v) => v.highlight_sets__status === 'draft')
    modal.confirm({
      title: `Restore version ${set?.highlight_sets__version} as a new version?`,
      content: `A new draft is made from this version: its narrative, items, hand edits and stored figures. The current version stays as it is until you approve the draft; Approve makes it current and signs it off. Refresh numbers on the draft brings its figures up to date first.${openDraft ? ` The open draft v${openDraft.highlight_sets__version} is set aside.` : ''}`,
      okText: 'Restore as new version',
      onOk: () => run(async () => {
        const r = await cloneHighlightSetDraft(set!.highlight_sets__id, token())
        openFormInTab('period-highlights', r.highlight_sets__id, `${set?.prog__scope_name ?? 'Highlights'} · ${set?.prog__period_label} v${r.highlight_sets__version}`)
      }, `Draft created from version ${set?.highlight_sets__version}`)
    })
  }

  const onSignOff = () => {
    modal.confirm({
      title: 'Sign off this highlight set?',
      content: reviewStatus === 'edited_since_sign_off'
        ? 'You confirm you have re-read the narrative and the stories after the edits. Reports then show the highlights as signed off.'
        : 'You confirm you have read the narrative and the stories, and edited anything that needed it. Reports then show the highlights as signed off instead of "not reviewed".',
      okText: 'Sign off',
      onOk: () => run(() => signOffHighlightSet(set!.highlight_sets__id, token()), 'Highlight set signed off')
    })
  }

  const onApprove = () => {
    modal.confirm({
      title: 'Approve this highlight set?',
      content: `Version ${set?.highlight_sets__version} becomes the current set for ${set?.prog__scope_name} · ${set?.prog__period_label}; ${set?.highlight_sets__scope_type === 'lp_account' ? 'the Investor Performance Report fills its highlight slots from it' : 'the quarterly report renders it'} and any previously approved version is superseded. You can still edit it in place afterwards.`,
      okText: 'Approve',
      onOk: () => run(() => approveHighlightSet(set!.highlight_sets__id, token()), 'Highlight set approved')
    })
  }

  const onFinaliseNow = () => {
    modal.confirm({
      title: `Finalise ${set?.prog__scope_name ?? 'this set'} · ${set?.prog__period_label} now?`,
      content: 'The live set is finalised as it stands: companies with a live set are frozen into their atoms with no model call, companies without one get an atom run, then the finalised version lands (approved by policy, awaiting sign-off). This live set keeps accumulating for late news.',
      okText: 'Finalise now',
      onOk: () => run(async () => {
        const r = await generateHighlights(finalisePayload(set!), token())
        const status = r?.enqueue?.status
        toast.info(status === 'merged' ? 'Merged into the queued run'
          : status === 'deferred' ? 'Queued behind the current run for this scope'
            : 'Finalisation queued — the finalised version appears in the list')
      }, '')
    })
  }

  const onRunAtom = (row: LeftOutRow) => {
    const name = row.name || 'the company'
    void run(async () => {
      const r = await generateHighlights({
        scope_type: 'company', account_id: row.account_id,
        period_start: set!.highlight_sets__period_start, period_end: set!.highlight_sets__period_end
      }, token())
      const status = r?.enqueue?.status
      toast.info(status === 'merged' ? `Merged into the run already queued for ${name}`
        : status === 'deferred' ? `Queued behind ${name}’s current run — Reformulate once the atom has landed`
          : `Atom run queued for ${name} — Reformulate once it has landed`)
    }, '')
  }

  const onReformulate = () => {
    modal.confirm({
      title: 'Ask the agent for a new version?',
      content: 'A new version is generated from the current books. The current version stays as it is until the new one lands (it is approved automatically where the policy says so).',
      okText: 'Reformulate',
      onOk: () => run(async () => {
        const r = await reformulateHighlightSet(set!.highlight_sets__id, token())
        const status = r?.enqueue?.status
        toast.info(status === 'deferred' ? 'Queued behind the current run for this scope' : 'Regeneration queued — a new version will appear in the list')
      }, '')
    })
  }

  const headerActions = set ? isLive ? (isRetiredLive ? null : (
    <span className='flex items-center gap-2'>
      <Button type='primary' size='small' loading={busy} onClick={onFinaliseNow}>Finalise now</Button>
    </span>
  )) : (
    <span className='flex items-center gap-2'>
      {isDraft && (
        <Button danger size='small' disabled={busy} onClick={() => { setRejectReason(''); setRejectOpen(true) }}>Reject</Button>
      )}
      <Button size='small' disabled={busy} onClick={onReformulate}>Reformulate</Button>
      {isEditable && (
        <>
          <Tooltip title={`Recompute every figure from the books; the wording stays as it is. A printed figure that moves by more than ${staleness?.tolerance_pct ?? 'the tolerance'}${staleness ? '%' : ''} asks for a new sign-off.`}>
            <Button size='small' disabled={busy} onClick={onRefreshMetrics}>Refresh numbers</Button>
          </Tooltip>
          <Button size='small' disabled={busy} onClick={startNarrative}>Edit narrative</Button>
          {!isCompanySummary && (
            <Tooltip title='Ask the agent to rewrite the narrative over the highlights selected now (one model call, queued). Hand edits to the narrative are replaced; a signed-off set asks for sign-off again.'>
              <Button size='small' disabled={busy || selectedRows.length === 0} onClick={onRegenerateNarrative}>Regenerate narrative</Button>
            </Tooltip>
          )}
          {!isCompanySummary && (
            <Tooltip title='Refresh the runners-up from today’s books: the strongest candidates the selection does not hold — stories new to the books first — as excluded rows you can swap in under the cap. Queued; no model call; nothing a report prints changes.'>
              <Button size='small' disabled={busy} onClick={onBench}>Add runners-up</Button>
            </Tooltip>
          )}
        </>
      )}
      {isDraft && <Button type='primary' size='small' loading={busy} onClick={onApprove}>Approve</Button>}
      {!isDraft && needsSignOff && <Button type='primary' size='small' loading={busy} onClick={onSignOff}>Sign off</Button>}
      {reviewStatus === 'signed_off' && <Button size='small' disabled={busy} onClick={onWithdrawSignOff}>Withdraw sign-off</Button>}
      {isSuperseded && <Button type='primary' size='small' loading={busy} onClick={onRestore}>Restore as new version</Button>}
    </span>
  ) : null

  const statusTag = set ? (STATUS_TAG[set.highlight_sets__status] || { color: 'default', label: set.highlight_sets__status }) : null
  const fmtDelta = (v: number | null | undefined) => v == null ? '—' : Math.abs(v) >= 1000 ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : v.toLocaleString(undefined, { maximumFractionDigits: 4 })
  const staleCategoryCount = (staleness?.items ?? []).filter((i) => i.stale_category).length
  const stalenessTip = staleness?.stale
    ? `${staleness.deltas.length} figure${staleness.deltas.length === 1 ? '' : 's'} moved by more than ${staleness.tolerance_pct}% since this set was generated` +
      (staleness.deltas.length ? ': ' + staleness.deltas.slice(0, 6).map((d) => `${d.point} ${d.key} ${fmtDelta(d.frozen)} → ${fmtDelta(d.live)}${d.delta_pct != null ? ` (${d.delta_pct > 0 ? '+' : ''}${d.delta_pct}%)` : ''}`).join('; ') + (staleness.deltas.length > 6 ? '; …' : '') : '') +
      (staleCategoryCount ? `. ${staleCategoryCount} item${staleCategoryCount === 1 ? '' : 's'} no longer supported by the books.` : '.') +
      ' Refresh numbers to resolve the tokens to the current figures, or Reformulate to regenerate.'
    : null
  // New in the books (2026-10-31): stories the books tell now that this
  // version does not hold — the company's activity in the period changed
  // after it was generated (a valuation booked late or re-dated into the
  // quarter). Refresh numbers never adds an item, so nothing else says so.
  const newStories = staleness?.new_stories ?? []
  const newInvestments = newStories.filter((s) => s.pinned).length
  const newStoriesTip = newStories.length ? (
    <span>
      {newStories.length === 1 ? 'A story' : `${newStories.length} stories`} the books tell now that this version does not hold: a transaction was booked into {set?.prog__period_label ?? 'the period'}, or moved, after it was generated.
      <ul style={{ margin: '4px 0', paddingLeft: 16 }}>
        {newStories.slice(0, 6).map((s) => (
          <li key={`${s.source_account_id}-${s.category_key}`}>{s.headline}{s.activity_now.length ? ` (${s.activity_now.join(', ')})` : ''}</li>
        ))}
        {newStories.length > 6 && <li>…</li>}
      </ul>
      {isEditable && (isCompanySummary
        ? 'Reformulate rebuilds this version from today’s books.'
        : `Add runners-up puts them first on the bench (no model call) to swap in, then Regenerate narrative; or Reformulate rebuilds this version from today’s books.${newInvestments ? ' A new investment only reaches the set through Reformulate.' : ''}`)}
    </span>
  ) : null
  const coverage = (set?.prog__coverage as CoverageNote | null) ?? null
  const missing = coverage?.missing?.length ?? 0
  // Who the tracker went ahead without (2026-09-09): the atom can be run by
  // hand from here, then Reformulate folds it into a new version.
  const leftOut = useMemo<LeftOutRow[]>(() => [
    ...(coverage?.unavailable ?? []),
    ...(coverage?.wait_expired ?? []).map((w) => ({ ...w, kind: 'wait_expired' as const, reason: 'still generating when the wait ran out' }))
  ], [coverage])
  const narrativeShown = set?.prog__narrative_resolved ?? set?.highlight_sets__narrative ?? null
  const narrativeEdited = Boolean(set?.highlight_sets__narrative_edited_at)

  return (
    <div ref={activateRef} data-component='HighlightSetForm' className='h-full overflow-hidden'>
      {contextHolder}
      <div className='w-full h-full flex flex-col bg-white px-4 py-3 overflow-hidden'>
        <div className='flex items-center gap-2' style={{ margin: '0 0 8px 0' }}>
          <Title level={4} style={{ margin: 0 }}>
            <FontAwesomeIcon icon={faStar} style={{ marginRight: 8 }} />
            {set ? `${set.prog__scope_name ?? 'Highlights'} · ${set.prog__period_label}` : 'Period Highlights'}
          </Title>
          {set && statusTag && (
            <span className='flex items-center gap-1.5 ml-2'>
              <Tag color={versionTag(set).color}>{versionTag(set).label}</Tag>
              {isCompanySummary && (
                <Tooltip title={companySummaryNote
                  ? `${set.prog__scope_name ?? 'This scope'} held ${companySummaryNote.companies.map((c) => c.name).filter(Boolean).join(' and ') || 'one company'} in ${set.prog__period_label} — ${companySummaryNote.count === 1 ? 'its only company' : `${companySummaryNote.count} companies`}, within the company-summary threshold of ${companySummaryNote.threshold} — so this version is a company update: a sectioned summary of the company’s quarter instead of ranked highlights. The reports print it as “Company update”.`
                  : 'This version is a company update: the scope held one company in the period, so the agent wrote a sectioned summary of its quarter instead of ranked highlights. The reports print it as “Company update”.'}>
                  <Tag color='geekblue'>Company summary</Tag>
                </Tooltip>
              )}
              {!set.highlight_sets__is_current && !isLive && <Tag color={statusTag.color}>{statusTag.label}</Tag>}
              {isLive && !isRetiredLive && (
                <Tooltip title='The open period’s accumulating set: the company followers flag developments as they read and the trackers re-rank them. Nothing here is rendered in a report until it is finalised.'>
                  <Tag color='blue'>Live</Tag>
                </Tooltip>
              )}
              {isRetiredLive && (
                <Tooltip title='This quarter is finalised. The agents stopped adding to this working copy and no report reads it; the current version is what the reports print.'>
                  <Tag color='default'>No longer used</Tag>
                </Tooltip>
              )}
              {set.highlight_sets__revision > 0 && (
                <Tooltip title={`Changed in place ${set.highlight_sets__revision} time${set.highlight_sets__revision === 1 ? '' : 's'} since it was generated: edits, and any Refresh numbers that moved a printed figure beyond the tolerance`}>
                  <Tag color='gold'>rev {set.highlight_sets__revision}</Tag>
                </Tooltip>
              )}
              {reviewStatus === 'signed_off' && (
                <Tooltip title={`Signed off${set.prog__signed_off_by_name ? ` by ${set.prog__signed_off_by_name}` : ''}${set.highlight_sets__signed_off_at ? ` on ${dayjs(set.highlight_sets__signed_off_at).format('MM/DD/YYYY')}` : ''} — reports show these highlights as reviewed.`}>
                  <Tag color='green'>Signed off</Tag>
                </Tooltip>
              )}
              {reviewStatus === 'unreviewed' && (
                <Tooltip title={withdrawal
                  ? `The sign-off${withdrawal.signed_off_by_name ? ` by ${withdrawal.signed_off_by_name}` : ''} was withdrawn${withdrawal.withdrawn_by_name ? ` by ${withdrawal.withdrawn_by_name}` : ''}${withdrawal.withdrawn_at ? ` on ${dayjs(withdrawal.withdrawn_at).format('MM/DD/YYYY')}` : ''}. Reports flag these highlights as not reviewed until someone presses Sign off.`
                  : 'Approved by policy; nobody has read it yet. Reports flag these highlights as not reviewed until someone presses Sign off.'}>
                  <Tag color='gold'>Not reviewed</Tag>
                </Tooltip>
              )}
              {reviewStatus === 'edited_since_sign_off' && (
                <Tooltip title={`Signed off${set.prog__signed_off_by_name ? ` by ${set.prog__signed_off_by_name}` : ''}, then ${figuresMovedSinceSignOff
                  ? `Refresh numbers${lastRefresh?.at ? ` on ${dayjs(lastRefresh.at).format('MM/DD/YYYY')}` : ''} moved a printed figure beyond the tolerance`
                  : 'edited'}. Re-read and press Sign off again.`}>
                  <Tag color='gold'>Edited since sign-off</Tag>
                </Tooltip>
              )}
              {staleness?.stale && (
                <Tooltip title={stalenessTip}><Tag color='orange'>Marks changed</Tag></Tooltip>
              )}
              {newStories.length > 0 && (
                <Tooltip title={newStoriesTip}><Tag color='orange'>New in the books</Tag></Tooltip>
              )}
              {set.prog__current_approved_set_id && set.prog__current_approved_set_id !== set.highlight_sets__id && (
                <button
                  type='button'
                  className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-xs'
                  onClick={() => openFormInTab('period-highlights', set.prog__current_approved_set_id as string, `${set.prog__scope_name ?? 'Highlights'} · ${set.prog__period_label} (current)`)}
                >
                  Open the current version
                </button>
              )}
            </span>
          )}
          <span className='ml-auto text-xs text-gray-500 flex items-center gap-3'>
            {set?.highlight_sets__generated_at && <span>{isLive ? 'Last touched' : 'Generated'} {dayjs(set.highlight_sets__generated_at).format('MM/DD/YYYY')}{set.internal_agent_types__name ? ` by ${set.internal_agent_types__name}` : ''}</span>}
            {set?.prog__approved_by_name && <span>Approved by {set.prog__approved_by_name}{set.highlight_sets__approved_at ? ` ${dayjs(set.highlight_sets__approved_at).format('MM/DD/YYYY')}` : ''}</span>}
            {set?.highlight_sets__reject_reason && <span>Rejected: {set.highlight_sets__reject_reason}</span>}
            {staleness && !staleness.stale && newStories.length === 0 && staleness.compared > 0 && (
              <Tooltip title={`${staleness.compared} figures recomputed from the books today; none moved by more than ${staleness.tolerance_pct}%.`}><span>Marks unchanged</span></Tooltip>
            )}
          </span>
        </div>

        <div className='flex-1 min-h-0 overflow-y-auto pr-1 flex flex-col gap-3'>
          <FormSection title={isCompanySummary ? 'Company update' : 'Narrative'} titleColor='blue' loading={loading} headerActions={headerActions} className='shadow-sm border border-gray-200'>
            {narrativeShown ? (
              <>
                <AgentMarkdown source={narrativeShown} />
                {(narrativeEdited || narrativeRegen || selectionChangedAfterNarrative) && (
                  <div className='mt-1 flex items-center gap-1.5 flex-wrap'>
                    {narrativeEdited && (
                      <Tooltip title={<span>Original: {set?.highlight_sets__original_narrative}</span>}>
                        <Tag color='gold'>Edited{set?.prog__narrative_edited_by_name ? ` by ${set.prog__narrative_edited_by_name}` : ''}</Tag>
                      </Tooltip>
                    )}
                    {narrativeRegen && !narrativeEdited && (
                      <Tooltip title={<span>Rewritten by the agent over the selection{narrativeRegen.at ? ` on ${dayjs(narrativeRegen.at).format('MM/DD/YYYY')}` : ''}{narrativeRegen.summary ? ` — ${narrativeRegen.summary}` : ''}{set?.highlight_sets__original_narrative ? <><br />Original: {set.highlight_sets__original_narrative}</> : null}</span>}>
                        <Tag color='geekblue'>Regenerated</Tag>
                      </Tooltip>
                    )}
                    {selectionChangedAfterNarrative && isEditable && !isCompanySummary && (
                      <Tooltip title='A highlight was selected or excluded after this narrative was written, so it may describe a different selection. Press Regenerate narrative to rewrite it over the current one, or Edit narrative.'>
                        <Tag color='orange'>Selection changed</Tag>
                      </Tooltip>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className='text-[13px] text-gray-500 italic'>
                {isRetiredLive
                  ? 'This quarter is finalised; this working copy is no longer used. It kept the quarter’s stories while the quarter was open — the current version is what the reports print.'
                  : isLive
                  ? (set?.highlight_sets__scope_type === 'company'
                    ? 'This is the open period’s live set: the follower adds a story the moment it reads one and re-measures the books items on every touch. It is finalised into the period’s atom — no narrative, no model call — when a fund or LP set is generated for the period.'
                    : 'This is the open period’s live set: re-ranked without a model call whenever a portfolio company flags something. The narrative is written when the period is finalised — on schedule, or with Finalise now.')
                  : set?.highlight_sets__scope_type === 'company' ? 'Company atoms carry items only; the fund or LP set writes the narrative.' : 'No narrative.'}
              </div>
            )}
            {coverage && coverage.expected != null && (
              <div className='mt-2 text-xs text-gray-500 flex items-center gap-2'>
                Coverage
                {missing > 0
                  ? <Tooltip title={describeCoverageGaps(coverage)}><Tag color='gold'>{coverage.found}/{coverage.expected} companies</Tag></Tooltip>
                  : <Tag color='green'>{coverage.found}/{coverage.expected} companies</Tag>}
                {set?.highlight_sets__params?.ranking_rationale && (
                  <Tooltip title={set.highlight_sets__params.ranking_rationale}><span className='underline decoration-dotted cursor-help'>ranking rationale</span></Tooltip>
                )}
              </div>
            )}
          </FormSection>

          {leftOut.length > 0 && !isLive && (
            <FormSection
              title='Companies not included'
              titleColor='orange'
              loading={loading}
              className='shadow-sm border border-gray-200'
              headerActions={
                <span className='text-gray-400 text-[11.5px]'>
                  {leftOut.length} of {coverage?.expected ?? '—'} portfolio companies
                </span>
              }
            >
              <div className='text-[13px] text-gray-600 mb-2'>
                These companies had no approved atom when this version was generated, so nothing of theirs was considered.
                Run atom asks the company follower for the period (a paused follower — an exited or dead company — is left out by the tracker but answers a request made here);
                once the atom has landed, Reformulate folds it into a new version.
              </div>
              <Table<LeftOutRow>
                size='small'
                rowKey='account_id'
                dataSource={leftOut}
                pagination={false}
                columns={[
                  {
                    title: 'Company', key: 'name', width: 260,
                    render: (_: unknown, r) => (
                      <button
                        type='button'
                        className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                        onClick={() => openFormInTab('companies', r.account_id, r.name || 'Company')}
                      >
                        {r.name || r.account_id}
                      </button>
                    )
                  },
                  {
                    title: 'Why', key: 'reason',
                    render: (_: unknown, r) => {
                      const t = LEFT_OUT_TAG[r.kind] || { color: 'default', label: r.kind }
                      return <span className='flex items-center gap-2'><Tag color={t.color}>{t.label}</Tag><span className='text-gray-600'>{r.reason || ''}</span></span>
                    }
                  },
                  {
                    title: '', key: 'action', width: 110,
                    render: (_: unknown, r) => r.kind === 'draft' && r.set_id
                      ? <Button size='small' onClick={() => openFormInTab('period-highlights', r.set_id as string, `${r.name || 'Company'} · ${set?.prog__period_label ?? ''}`)}>Open atom</Button>
                      : <Button size='small' disabled={busy} onClick={() => onRunAtom(r)}>Run atom</Button>
                  }
                ]}
              />
            </FormSection>
          )}

          <FormSection
            title='Items'
            titleColor='blue'
            loading={loading}
            className='shadow-sm border border-gray-200'
            headerActions={
              <span className='text-gray-400 text-[11.5px]'>
                {selectedCount} of {includedCap} selected{pinnedCount ? ` · ${pinnedCount} pinned` : ''} · {benchRows.length} runner{benchRows.length === 1 ? '-up' : 's-up'} / excluded
                {isRetiredLive ? ' · no longer used' : isLive ? ' · live, kept by the agents' : !isEditable ? ' · read-only' : ''}
              </span>
            }
          >
            {([['selected', selectedRows], ['bench', benchRows]] as const).map(([group, rows]) => (
            <div key={group} className={group === 'bench' ? 'mt-3' : ''} data-group={group}>
              <div className='text-xs font-semibold text-gray-600 mb-1 flex items-center gap-2'>
                {group === 'selected' ? (
                  <>Selected <span className='font-normal text-gray-400'>— printed in the reports, in this order</span></>
                ) : (
                  <>
                    Also considered
                    <Tooltip title='The next candidates the ranking offered: the agent worded them but did not select them, or the reviewer excluded them. Switch one on to swap it into the report — up to the cap; exclude a selected item first when the cap is reached.'>
                      <span className='font-normal text-gray-400'>— runners-up and excluded items; switch one on to swap it in</span>
                    </Tooltip>
                  </>
                )}
              </div>
            <Table<HighlightItemRecord>
              size='small'
              rowKey='highlight_items__id'
              dataSource={rows}
              pagination={false}
              scroll={{ x: 'max-content' }}
              rowClassName={(row) => (row.highlight_items__status === 'included' ? '' : 'opacity-60')}
              locale={{ emptyText: group === 'selected' ? 'No items selected.'
                : isCompanySummary ? 'A company update keeps every candidate — there are no runners-up.'
                  : 'No runners-up on this version — Add runners-up (above) queues a pass that appends the next candidates.' }}
              columns={[
                {
                  title: '', key: 'include', width: 56,
                  render: (_: unknown, r) => isPinned(r) ? (
                    <Tooltip title='A new-investment introduction is always included — the quarterly report prints it in New Investments in Period. Edit its wording instead.'>
                      <Switch size='small' checked disabled />
                    </Tooltip>
                  ) : (
                    <Tooltip title={r.highlight_items__status !== 'included' && atCap
                      ? `At most ${includedCap} highlights can be selected (the reports print that many) — exclude one first`
                      : undefined}>
                      <Switch
                        size='small'
                        checked={r.highlight_items__status === 'included'}
                        disabled={!isEditable || busy || (r.highlight_items__status !== 'included' && atCap)}
                        onChange={(v) => run(() => editHighlightItem(r.highlight_items__id, { status: v ? 'included' : 'excluded' }, token()).then(() => {}), '')}
                      />
                    </Tooltip>
                  )
                },
                { title: '#', dataIndex: 'highlight_items__sequence', key: 'seq', width: 44, align: 'right' as const },
                {
                  title: 'Category', key: 'category', width: 150,
                  render: (_: unknown, r) => (
                    <span className='flex items-center gap-1'>
                      <Tag color={POLARITY_TAG[r.highlight_categories__polarity] ?? 'default'}>{r.highlight_categories__label}</Tag>
                      {r.highlight_categories__deterministic && <Tooltip title='Figures computed from the books; the agent only worded it'><Tag color='purple'>Books</Tag></Tooltip>}
                      {isPositionItem(r) && (
                        <Tooltip title={"This scope's own position: the figures are this fund's (or, for an investor, the see-through share) fair value, cost, investment or commitment — not the firm's across every fund"}>
                          <Tag color='cyan'>Position</Tag>
                        </Tooltip>
                      )}
                      {isPinned(r) && (
                        <Tooltip title='Pinned: the company introduction the quarterly report prints under New Investments in Period — always included; edit its wording here'>
                          <Tag color='geekblue'>Pinned</Tag>
                        </Tooltip>
                      )}
                      {isBench(r) && (
                        <Tooltip title={r.highlight_items__status === 'included'
                          ? 'A runner-up the reviewer swapped in: the ranking offered it, the agent did not select it.'
                          : 'A runner-up: the ranking offered it and the agent did not select it. Switch it on to swap it into the report.'}>
                          <Tag>Runner-up</Tag>
                        </Tooltip>
                      )}
                    </span>
                  )
                },
                {
                  title: 'Headline / body', key: 'text',
                  render: (_: unknown, r) => (
                    <div className='text-[13px]'>
                      <div className='font-semibold text-gray-800 flex items-center gap-2 flex-wrap'>
                        {r.prog__headline_resolved ?? r.highlight_items__headline}
                        {r.prog__is_edited && (
                          <Tooltip title={<span>Original: {r.highlight_items__original_headline}{r.highlight_items__original_body ? ` — ${r.highlight_items__original_body}` : ''}</span>}>
                            <Tag color='gold'>Edited</Tag>
                          </Tooltip>
                        )}
                        {(r.highlight_items__stale_category || itemStaleness[r.highlight_items__id]?.stale_category) && (
                          <Tooltip title='The books no longer produce this item (e.g. a markup restated into a markdown). Reports leave it out until the set is reformulated.'>
                            <Tag color='red'>Category changed</Tag>
                          </Tooltip>
                        )}
                        {!r.highlight_items__stale_category && (itemStaleness[r.highlight_items__id]?.moved ?? 0) > 0 && (
                          <Tooltip title={`${itemStaleness[r.highlight_items__id].moved} figure${itemStaleness[r.highlight_items__id].moved === 1 ? '' : 's'} moved since this item was generated — Refresh numbers resolves its tokens to the current values.`}>
                            <Tag color='orange'>Marks changed</Tag>
                          </Tooltip>
                        )}
                        {r.prog__source_account_name && r.highlight_items__source_account_id && (
                          <button
                            type='button'
                            className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-xs font-normal'
                            onClick={() => openFormInTab('companies', r.highlight_items__source_account_id as string, r.prog__source_account_name || 'Company')}
                          >
                            {r.prog__source_account_name}
                          </button>
                        )}
                      </div>
                      {(r.prog__body_resolved ?? r.highlight_items__body) && <div className='text-gray-700'>{r.prog__body_resolved ?? r.highlight_items__body}</div>}
                      {r.highlight_items__short_text && r.highlight_items__short_text !== r.highlight_items__headline && (
                        <div className='text-gray-500 text-xs mt-0.5'>Short: {r.prog__short_text_resolved ?? r.highlight_items__short_text}</div>
                      )}
                      {heldThrough(r).length > 0 && (
                        <div className='text-gray-500 text-xs mt-0.5'>
                          Held through: {heldThrough(r).map((f) => `${f.name}${f.share != null ? ` (${(f.share * 100).toFixed(1)}%)` : ''}`).join(', ')}
                        </div>
                      )}
                    </div>
                  )
                },
                {
                  title: 'Materiality', key: 'materiality', width: 100, align: 'right' as const,
                  render: (_: unknown, r) => r.highlight_items__materiality != null ? r.highlight_items__materiality.toFixed(2) : '—'
                },
                {
                  title: '', key: 'actions', width: 210, fixed: 'right' as const,
                  render: (_: unknown, r, idx) => (
                    <span className='flex items-center gap-1'>
                      <Button size='small' onClick={() => setEvidenceFor(r)}>Evidence</Button>
                      {isEditable && (
                        <>
                          <Button size='small' disabled={busy} onClick={() => startEdit(r)}>Edit</Button>
                          <Button size='small' disabled={busy || idx === 0} onClick={() => move(r, -1)}>↑</Button>
                          <Button size='small' disabled={busy || idx === rows.length - 1} onClick={() => move(r, 1)}>↓</Button>
                        </>
                      )}
                    </span>
                  )
                }
              ]}
            />
            </div>
            ))}
          </FormSection>

          {detail && detail.versions.length > 1 && (
            <FormSection title='Versions' titleColor='gray' className='shadow-sm border border-gray-200'>
              <ul className='list-none m-0 p-0 text-[13px] flex flex-col gap-1'>
                {detail.versions.map((v) => (
                  <li key={v.highlight_sets__id} className='flex items-center gap-2'>
                    <Tag color={versionTag(v).color}>{versionTag(v).label}</Tag>
                    <span className='text-gray-500 text-xs'>
                      {v.highlight_sets__generated_at ? `generated ${dayjs(v.highlight_sets__generated_at).format('MM/DD/YYYY')}` : ''}
                      {v.highlight_sets__approved_at ? ` · approved ${dayjs(v.highlight_sets__approved_at).format('MM/DD/YYYY')}` : ''}
                    </span>
                    {v.highlight_sets__id !== set?.highlight_sets__id && (
                      <button
                        type='button'
                        className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-xs'
                        onClick={() => openFormInTab('period-highlights', v.highlight_sets__id, `${set?.prog__scope_name ?? 'Highlights'} · ${set?.prog__period_label} v${v.highlight_sets__version}`)}
                      >
                        Open
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </FormSection>
          )}
        </div>
      </div>

      <EvidenceDrawer item={evidenceFor} onClose={() => setEvidenceFor(null)} />

      <Modal
        title='Edit item'
        open={editing != null}
        onOk={saveEdit}
        okText='Save'
        okButtonProps={{ disabled: !draft.headline.trim() || draft.headline.trim().length > caps.headline || draft.body.trim().length > caps.body || draft.short_text.trim().length > caps.short_text }}
        confirmLoading={busy}
        onCancel={() => setEditing(null)}
        destroyOnHidden
        width={680}
      >
        {editing && (
          <div className='flex flex-col gap-3 text-[13px]'>
            <div>
              <div className='text-gray-600 mb-1'>Figures <span className='text-gray-400'>(click to insert into the field you last edited)</span></div>
              <TokenChips tokens={(editing.prog__metric_tokens ?? []) as MetricToken[]} onPick={pickItemToken} />
            </div>
            <div>
              <div className='text-gray-600 mb-1'>Headline <span className='text-gray-400'>({draft.headline.trim().length}/{caps.headline})</span></div>
              <Input ref={headlineRef} value={draft.headline} maxLength={caps.headline} onFocus={() => setActiveField('headline')} onChange={(e) => setDraft({ ...draft, headline: e.target.value })} />
              <div className='text-xs text-gray-500 mt-0.5'>{previewTokens(draft.headline, (editing.prog__metric_tokens ?? []) as MetricToken[])}</div>
            </div>
            <div>
              <div className='text-gray-600 mb-1'>Body <span className='text-gray-400'>({draft.body.trim().length}/{caps.body})</span></div>
              <Input.TextArea ref={bodyRef} rows={3} value={draft.body} maxLength={caps.body} onFocus={() => setActiveField('body')} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
              <div className='text-xs text-gray-500 mt-0.5'>{previewTokens(draft.body, (editing.prog__metric_tokens ?? []) as MetricToken[])}</div>
            </div>
            <div>
              <div className='text-gray-600 mb-1'>Short text (deck slot) <span className='text-gray-400'>({draft.short_text.trim().length}/{caps.short_text})</span></div>
              <Input.TextArea ref={shortRef} rows={2} value={draft.short_text} maxLength={caps.short_text} onFocus={() => setActiveField('short_text')} onChange={(e) => setDraft({ ...draft, short_text: e.target.value })} />
              <div className='text-xs text-gray-500 mt-0.5'>{previewTokens(draft.short_text, (editing.prog__metric_tokens ?? []) as MetricToken[])}</div>
            </div>
            <div className='text-xs text-gray-500'>
              {editing.highlight_categories__deterministic
                ? 'Figures on a Books item are best written as tokens — a typed number never follows the books and is saved with a warning. Company and fund names may be written as they are.'
                : 'A figure is accepted when the cited evidence or the stored wording states it; any other typed number is saved with a warning.'}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title='Edit narrative'
        open={narrativeOpen}
        onOk={saveNarrative}
        okText='Save'
        okButtonProps={{ disabled: narrativeDraft.trim().length > narrativeMax }}
        confirmLoading={busy}
        onCancel={() => setNarrativeOpen(false)}
        destroyOnHidden
        width={760}
      >
        <div className='flex flex-col gap-3 text-[13px]'>
          <div>
            <div className='text-gray-600 mb-1'>Figures <span className='text-gray-400'>(click to insert at the cursor)</span></div>
            <TokenChips tokens={setTokens} onPick={pickNarrativeToken} />
          </div>
          <div>
            <div className='text-gray-600 mb-1'>{isCompanySummary ? 'Company update' : 'Narrative'} <span className='text-gray-400'>({narrativeDraft.trim().length}/{narrativeMax}; markdown{isCompanySummary ? '; sections as ## headings' : ''})</span></div>
            <Input.TextArea ref={narrativeRef} rows={isCompanySummary ? 16 : 8} value={narrativeDraft} maxLength={narrativeMax} onChange={(e) => setNarrativeDraft(e.target.value)} />
          </div>
          <div>
            <div className='text-gray-600 mb-1'>Preview</div>
            <div className='border border-gray-200 rounded p-2 bg-gray-50'>
              <AgentMarkdown source={previewTokens(narrativeDraft, setTokens) || '_(empty)_'} />
            </div>
          </div>
          <div className='text-xs text-gray-500'>Fund and portfolio figures are best written as tokens — a typed number never follows the books. A figure from one of the included stories or from the stored text may be repeated as it reads there; any other typed number is saved with a warning.</div>
        </div>
      </Modal>

      <Modal
        title='Reject this highlight set?'
        open={rejectOpen}
        onOk={() => { setRejectOpen(false); void run(() => rejectHighlightSet(set!.highlight_sets__id, rejectReason.trim(), token()), 'Highlight set rejected') }}
        okText='Reject'
        okButtonProps={{ danger: true }}
        cancelText='Keep'
        confirmLoading={busy}
        onCancel={() => setRejectOpen(false)}
        destroyOnHidden
      >
        <div className='text-[13px] text-gray-700 mb-2'>The draft is closed; Reformulate later produces a fresh version.</div>
        <Input.TextArea rows={3} placeholder='Reason (optional)' value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
      </Modal>
    </div>
  )
}

export default HighlightSetForm
